import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { PageHeader, Panel, useAdminSettings } from "@/pages/admin/shared";
import { UploadButton } from "@/pages/admin/UploadButton";

const SLOTS = [
  ["poster_mobile_url", "Mobile poster (vertical)", "aspect-[2/3]"],
  ["poster_desktop_url", "Desktop poster (landscape)", "aspect-[3/2]"],
  ["logo_url", "TSS logo (top bar)", "aspect-square"],
  ["award_logo_url", "Sharod Shamman logo (hero)", "aspect-square"],
];

const Slot = ({ field, label, aspect, value, media, onSet }) => (
  <Panel data-testid={`hero-slot-${field}`}>
    <h3 className="font-display text-lg font-bold text-maroon">{label}</h3>
    <div className={`mt-3 overflow-hidden rounded-xl border border-gold/30 bg-ivory ${aspect} max-h-72`}>
      {value ? <img src={mediaUrl(value)} alt={label} className="h-full w-full object-contain" /> : <div className="hero-placeholder grid h-full place-items-center text-xs text-ivory/80">Gradient placeholder</div>}
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <UploadButton testId={`hero-upload-${field}`} label="Upload / replace" onUploaded={(url) => onSet(field, url)} />
      {value && <button type="button" onClick={() => onSet(field, "")} data-testid={`hero-clear-${field}`} className="text-xs text-maroon underline">Clear</button>}
    </div>
    {media.length > 0 && (
      <div className="mt-3">
        <p className="mb-1 text-xs text-ink-muted">Or pick from uploaded images:</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {media.map((m) => (
            <button key={m.id} type="button" onClick={() => onSet(field, m.url)} data-testid={`hero-pick-${field}-${m.id}`} className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 ${value === m.url ? "border-forest" : "border-transparent"}`}>
              <img src={mediaUrl(m.url)} alt={m.name} className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </div>
    )}
  </Panel>
);

export default function HeroAdmin() {
  const { settings, save } = useAdminSettings();
  const [media, setMedia] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");

  const loadMedia = () => api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => { loadMedia(); }, []);
  useEffect(() => { if (settings) { setTitle(settings.hero_title); setSubtitle(settings.hero_subtitle); } }, [settings]);

  const setSlot = async (field, url) => { await save({ [field]: url }, "Hero updated — live now"); loadMedia(); };

  return (
    <div data-testid="admin-hero">
      <PageHeader title="Hero & posters" sub="Mobile shows the vertical poster, desktop the landscape one" />
      <Panel className="mb-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm font-medium">Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="hero-title-input" className="mt-1 h-11 w-full rounded-xl border border-gold/40 bg-ivory px-3 outline-none focus:border-gold" />
          </label>
          <label className="text-sm font-medium">Subtitle
            <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} data-testid="hero-subtitle-input" className="mt-1 h-11 w-full rounded-xl border border-gold/40 bg-ivory px-3 outline-none focus:border-gold" />
          </label>
        </div>
        <button type="button" onClick={() => save({ hero_title: title, hero_subtitle: subtitle }, "Hero text saved")} disabled={!title.trim()} data-testid="hero-text-save-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40">
          <Save className="h-4 w-4" /> Save text
        </button>
      </Panel>
      {settings && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {SLOTS.map(([f, l, a]) => <Slot key={f} field={f} label={l} aspect={a} value={settings[f]} media={media} onSet={setSlot} />)}
        </div>
      )}
    </div>
  );
}
